/**
 * The report options of an MD job's results: pick the compounds and sections
 * to include and a format (PDF or PowerPoint), and download. The charts are
 * drawn to pictures in the browser from the same options the screen uses, so
 * the report holds what the user sees. New sections join the report by being
 * added to MD_REPORT_SECTIONS (integrations/mdReport.js).
 */
import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import Select from 'BMapsUI/common/Select';
import { downloadData } from 'BMapsSrc/ui/ui_utils';
import {
    MD_REPORT_FORMATS, buildReportModel, reportCompounds, reportSectionsFor,
} from 'BMapsSrc/integrations/mdReport';
import { writeReport } from 'BMapsSrc/integrations/mdReportExport';
import { renderOptionToPng } from 'BMapsSrc/ui/charting/renderChartImage';
import { renderSvgToPng } from 'BMapsSrc/ui/charting/renderSvgToPng';
import { CheckboxGroup } from '../FreeEnergyPane/Tabs/NewFreeEnergyJob';

/**
 * @param {object} props
 * @param {object} props.md - the job's md/1 packet
 * @param {string} props.title - the job's name
 * @param {string} [props.method] - the engine label
 * @param {Function} [props.renderImage] - draws a chart option to a picture (replaceable in tests)
 * @param {Function} [props.renderSvg] - draws an SVG string to a picture (replaceable in tests)
 * @param {Function} [props.write] - writes the report file (replaceable in tests)
 * @param {Function} [props.download] - saves the file (replaceable in tests)
 */
export default function MdReportPanel({
    md, title, method,
    renderImage = renderOptionToPng, renderSvg = renderSvgToPng,
    write = writeReport, download = downloadData,
}) {
    const compounds = useMemo(() => reportCompounds(md), [md]);
    const sections = useMemo(() => reportSectionsFor(compounds), [compounds]);
    const [format, setFormat] = useState('pdf');
    const [names, setNames] = useState(() => compounds.map((c) => c.name));
    const [sectionIds, setSectionIds] = useState(() => sections.map((s) => s.id));
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState();

    if (compounds.length === 0) return null;

    function toggle(list, setList) {
        return (key, checked) => setList(checked
            ? [...list.filter((k) => k !== key), key] : list.filter((k) => k !== key));
    }

    let disabled = '';
    if (names.length === 0) disabled = 'Select at least one compound';
    else if (sectionIds.length === 0) disabled = 'Select at least one section';

    // A chart is drawn from its ECharts option, the interaction diagram from its SVG.
    const pictureOf = (section) => (section.svg
        ? renderSvg(section.svg, section.size) : renderImage(section.option, section.size));

    async function onDownload() {
        setBusy(true);
        setMessage(undefined);
        try {
            const model = buildReportModel({
                md,
                title,
                method,
                compoundNames: names,
                sectionIds,
                generated: new Date().toISOString().slice(0, 10),
            });
            for (const compound of model.compounds) {
                for (const section of compound.sections) {
                    // eslint-disable-next-line no-await-in-loop
                    section.image = await pictureOf(section);
                }
            }
            const { data, mime, fileName } = await write(model, format);
            download(fileName, data, mime);
            setMessage(`Saved ${fileName}.`);
        } catch (error) {
            setMessage(`Could not make the report: ${error.message || error}`);
        } finally {
            setBusy(false);
        }
    }

    return (
        <DialogContentSection title="Report" titleVariant="subtitle2" titleUpperCase={false} collapsible>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <CheckboxGroup
                    label="Compounds"
                    items={compounds}
                    selectedKeys={names}
                    getKey={(c) => c.name}
                    getName={(c) => c.name}
                    onToggle={toggle(names, setNames)}
                />
                <CheckboxGroup
                    label="Sections"
                    items={sections}
                    selectedKeys={sectionIds}
                    getKey={(s) => s.id}
                    getName={(s) => s.title}
                    onToggle={toggle(sectionIds, setSectionIds)}
                />
            </Box>
            <Select
                id={`md-report-format-${title}`}
                label="Format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                sx={{ width: '100%', margin: '0.5rem 0' }}
            >
                {MD_REPORT_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </Select>
            <DialogPrimaryButton
                id={`md-report-download-${title}`}
                disabled={!!disabled || busy}
                onClick={onDownload}
                title={disabled}
            >
                {busy ? 'Making the report…' : 'Download report'}
            </DialogPrimaryButton>
            {message && (
                <Typography component="div" sx={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    {message}
                </Typography>
            )}
        </DialogContentSection>
    );
}
